import { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { serverStore } from '../../stores/server.store';
import { ServerDiagnosisProgress } from '../ai/ServerDiagnosisProgress';
import { FirewallPortsCard } from '../server/FirewallPortsCard';
import { ServerStatTiles } from '../server/ServerStatTiles';
import { PlayerSummaryCard } from '../server/PlayerSummaryCard';
import { QuickActions } from '../server/QuickActions';
import { QuickSettingsCard } from '../server/QuickSettingsCard';
import { ActivityCard } from '../server/steam/ActivityCard';
import { McHero } from '../server/mc/McHero';
import { McWorldCard } from '../server/mc/McWorldCard';
import type { McOverview } from '../server/mc/types';
import { formatBytes } from '../../utils/overview-format';
import { cn } from '@/utils/cn';

const POLL_MS = 15000;
const POLL_STARTING_MS = 5000;

/**
 * Übersicht für Minecraft-Server – dieselbe Sprache wie die Steam-Übersicht
 * (Entwurf „A · Spielbühne"): Bühne mit Bereit-Status und Beitreten, die
 * Live-Kacheln, Spieler + „Welt & Server" neben Schnelleinstellungen und
 * Quick Actions, darunter Aktivität/Log. Daten: GET /mc/overview.
 */
export const MinecraftDashboard = observer(() => {
  const { activeServer } = serverStore;
  const id = activeServer?.id;
  const status = activeServer?.status;
  const [overview, setOverview] = useState<McOverview | null>(null);
  const [fw, setFw] = useState<any>(null);
  const starting = overview?.ready === 'waiting';

  useEffect(() => { setOverview(null); }, [id]);

  useEffect(() => {
    if (!id) return;
    let alive = true;
    const load = async () => {
      const o = await serverStore.fetchMcOverview(id);
      if (alive && o) setOverview(o);
      if (serverStore.activeServer?.status === 'online') serverStore.fetchRuntimeDetails(id);
    };
    load();
    // Beim Hochfahren öfter nachsehen, damit „Bereit zum Spielen" zeitnah erscheint.
    const t = setInterval(load, starting ? POLL_STARTING_MS : POLL_MS);
    return () => { alive = false; clearInterval(t); };
  }, [id, status, starting]);

  // Firewall nur beim Öffnen prüfen - jeder Check ist ein sudo-Aufruf.
  useEffect(() => {
    if (!id) return;
    let alive = true;
    setFw(null);
    serverStore.fetchFirewall(id).then((d) => { if (alive) setFw(d); });
    return () => { alive = false; };
  }, [id]);

  if (!activeServer) return null;
  const online = status === 'online';
  const runtime = serverStore.serverRuntimeDetails.get(activeServer.id)?.playerCount;
  const names: string[] = online ? runtime?.players || [] : [];
  const portsMissing = fw && fw.checked && !fw.allOpen;
  const isBedrock = activeServer.type === 'bedrock';

  const saveMaxPlayers = async (n: number) => {
    const r = await serverStore.saveProperties(activeServer.id, { 'max-players': n });
    if (r) setOverview((o) => (o ? { ...o, maxPlayers: n } : o));
    return { ok: !!r, restartNeeded: !!r?.restartNeeded?.length };
  };

  return (
    <div className="space-y-6">
      <ServerDiagnosisProgress />

      {overview
        ? <McHero overview={overview} server={activeServer} firewall={fw} playerNames={names} maxPlayers={runtime?.max || overview.maxPlayers} />
        : <div className="h-[272px] rounded-2xl border border-line bg-surface animate-pulse" />}

      {portsMissing && <FirewallPortsCard serverId={activeServer.id} />}

      <ServerStatTiles diskSub={overview?.world.bytes ? `Server-Daten · davon Welt ${formatBytes(overview.world.bytes)}` : undefined} />

      {/* Online: rechts stehen die hohen Quick Actions – nur die LINKE Spalte wird auf Zeilenhöhe
          gestreckt, und darin wächst nur die Aktivität bis zur gleichen Unterkante. Die Hüllen
          (shrink-0) verhindern, dass Karten mit h-full sich strecken oder gequetscht werden. */}
      <div className={cn('grid grid-cols-1 lg:grid-cols-2 gap-6', !online && 'items-start')}>
        <div className={cn('min-w-0', online ? 'flex flex-col gap-6' : 'space-y-6')}>
          {online && <div className="shrink-0"><PlayerSummaryCard /></div>}
          {overview && <div className="shrink-0"><McWorldCard overview={overview} server={activeServer} online={online} /></div>}
          {online && <ActivityCard fill />}
        </div>
        <div className="space-y-6 min-w-0 self-start">
          <QuickSettingsCard maxPlayers={overview?.maxPlayers ?? null} onSaveMaxPlayers={saveMaxPlayers} maxPlayersLimit={isBedrock ? 200 : 500} />
          {online && <QuickActions />}
        </div>
      </div>

      {!online && <ActivityCard />}
    </div>
  );
});
